import ProductServiceAPI from '../../../api/ProductServiceAPI';
import type {
  SopExtraction,
  SopRule,
  SopRuleCondition,
} from '../../../api/SopRulesAPI';

/**
 * Import Hub "Database Rules": rulesets saved on, and applied to, one database
 * by the product service. Applying and undoing run as background jobs.
 */

export type RulesetJobKind = 'apply' | 'deactivate' | 'delete';

export interface RulesetApplySummary {
  kind: RulesetJobKind;
  totalProducts: number;
  matchedProducts: number;
  updated: number;
  unchanged: number;
  inapplicable: number;
  keptUserChanges: number;
  failed: number;
  failures: Array<{ database_product_id: string; error: string }>;
  completedAt: string;
}

export interface ImportRulesetRule {
  id: string;
  position: number;
  title: string;
  detail: string;
  action_type: Exclude<SopRule['action']['type'], 'none'>;
  code: string;
  code_label: string;
  conditions: SopRuleCondition[];
  section: string;
  page: number | null;
  quote: string;
  /** The rule's id in the extraction it came from (r1, r2, ...). */
  source_rule_id: string | null;
}

export interface ImportRuleset {
  id: string;
  database_id: string;
  name: string;
  source_filename: string | null;
  source_extraction_id: string | null;
  is_active: boolean;
  created_by: string;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
  applied_at: string | null;
  applied_by: string | null;
  last_apply_summary: RulesetApplySummary | null;
  has_source_snapshot: boolean;
  /** Products the ruleset has changed, as View changes counts them (listed rulesets only). */
  changed_products?: number;
  /** Codes it set that a user has since changed by hand (listed rulesets only). */
  manual_changes?: number;
  rules: ImportRulesetRule[];
}

/** The SOP extraction a ruleset was built from, saved so Load works after pyapi expires it. */
export interface RulesetSourceSnapshot {
  filename: string | null;
  page_count: number | null;
  warnings: string[];
  rules: SopRule[];
}

export interface RulesetCodeChange {
  kind: 'fta' | 'exclusion';
  code: string;
  label: string | null;
  previous_value: boolean;
  applied_value: boolean;
  /** The product's value now; differs from applied_value once a user changed it. */
  current_value: boolean | null;
}

export interface RulesetChangedProduct {
  database_product_id: string;
  product_id: string;
  name: string;
  hts_code: string;
  country_of_origin: string | null;
  duty_before: number | null;
  duty_now: number | null;
  applied_at: string;
  changes: RulesetCodeChange[];
}

export interface RulesetChangesPage {
  page: number;
  page_size: number;
  /** Duty totals cover only products with both amounts. */
  totals: {
    products: number;
    compared_products: number;
    duty_before: number;
    duty_now: number;
  };
  products: RulesetChangedProduct[];
}

export interface ProductRulesetEffects {
  ruleset: { id: string; name: string } | null;
  effects: Array<Omit<RulesetCodeChange, 'current_value'>>;
}

export interface ImportRulesetJob {
  jobId: string;
  databaseId: string;
  rulesetId: string;
  kind: RulesetJobKind;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  processedProducts: number;
  totalProducts: number;
  summary?: RulesetApplySummary;
  error?: string;
}

const POLL_INTERVAL_MS = 1500;

export const RULESET_JOB_VERBS: Record<RulesetJobKind, string> = {
  apply: 'Applying',
  deactivate: 'Turning off',
  delete: 'Removing',
};

const plural = (count: number, word: string) =>
  `${count.toLocaleString()} ${word}${count === 1 ? '' : 's'}`;

export const formatRulesetDay = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

const attentionParts = (summary: RulesetApplySummary): string[] =>
  [
    summary.inapplicable > 0 && `${summary.inapplicable} not applicable`,
    summary.keptUserChanges > 0 &&
      `${plural(summary.keptUserChanges, 'manual change')} kept`,
    summary.failed > 0 && `${summary.failed} failed`,
  ].filter((part): part is string => !!part);

/** What one apply run did. */
export const rulesetSummaryParts = (summary: RulesetApplySummary): string[] => [
  `${plural(summary.updated, 'product')} updated`,
  ...attentionParts(summary),
];

/**
 * An active ruleset's standing: the products it has changed so far, not just
 * the last run (a re-apply that changes nothing would read "0 updated").
 */
export const rulesetStatusParts = (ruleset: ImportRuleset): string[] => [
  `${plural(ruleset.changed_products ?? 0, 'product')} changed`,
  ...(ruleset.last_apply_summary
    ? attentionParts(ruleset.last_apply_summary)
    : []),
];

/** For apply confirmations: an explicit apply sets every matched code. */
export const manualChangeNote = (manualChanges: number): string =>
  'Where a rule matches a product, its setting replaces any change made to that FTA or exclusion by hand.' +
  (manualChanges > 0
    ? ` ${plural(
        manualChanges,
        'manual change',
      )} made since the last apply will be replaced.`
    : '');

const errorText = (result: unknown, fallback: string) =>
  typeof result === 'string' && result ? result : fallback;

/** Only rules BITE can apply are saved; their extraction id is kept for Load. */
export const toRulesetRuleInput = (rule: SopRule) => ({
  title: rule.title,
  detail: rule.detail,
  action_type: rule.action.type,
  code: rule.action.code,
  code_label: rule.action.code_label,
  conditions: rule.conditions,
  section: rule.section,
  page: rule.page,
  quote: rule.quote,
  source_rule_id: rule.id,
});

export const fetchImportRulesets = async (
  databaseId: string,
): Promise<{ success: boolean; rulesets: ImportRuleset[]; error?: string }> => {
  const { success, result } = await ProductServiceAPI.getDatabaseRulesets(
    databaseId,
  );
  return success && Array.isArray(result)
    ? { success: true, rulesets: result as ImportRuleset[] }
    : {
        success: false,
        rulesets: [],
        error: errorText(result, 'Failed to load rulesets'),
      };
};

const toSourceSnapshot = (
  extraction: SopExtraction | null,
): RulesetSourceSnapshot | undefined =>
  extraction?.result
    ? {
        filename: extraction.filename || null,
        page_count: extraction.result.page_count,
        warnings: extraction.result.warnings,
        rules: extraction.result.rules,
      }
    : undefined;

/**
 * Rebuild the extraction a ruleset came from out of its saved snapshot.
 * Rulesets saved before snapshots existed return null.
 */
export const fetchRulesetSourceExtraction = async (
  databaseId: string,
  ruleset: ImportRuleset,
): Promise<SopExtraction | null> => {
  if (!ruleset.has_source_snapshot) return null;
  const { success, result } = await ProductServiceAPI.getDatabaseRuleset(
    databaseId,
    ruleset.id,
  );
  const snapshot = success
    ? (result as { source_snapshot?: RulesetSourceSnapshot | null })
        ?.source_snapshot
    : null;
  if (!snapshot) return null;
  return {
    extraction_id: ruleset.source_extraction_id ?? '',
    status: 'complete',
    stage: 'done',
    filename: snapshot.filename ?? ruleset.source_filename ?? '',
    database_id: databaseId,
    progress: { chunks_done: 0, chunks_total: 0 },
    result: {
      rules: snapshot.rules,
      page_count: snapshot.page_count,
      model: '',
      warnings: snapshot.warnings,
    },
    error: null,
    created_at: ruleset.created_at,
    updated_at: ruleset.updated_at,
  };
};

export const saveImportRuleset = async (
  databaseId: string,
  input: {
    rulesetId?: string;
    name: string;
    sourceFilename: string | null;
    sourceExtractionId: string | null;
    extraction: SopExtraction | null;
    rules: SopRule[];
  },
): Promise<{ success: boolean; ruleset?: ImportRuleset; error?: string }> => {
  const rules = input.rules.map(toRulesetRuleInput);
  const sourceSnapshot = toSourceSnapshot(input.extraction);
  const { success, result } = input.rulesetId
    ? await ProductServiceAPI.updateDatabaseRuleset(
        databaseId,
        input.rulesetId,
        {
          name: input.name,
          rules,
          ...(sourceSnapshot ? { source_snapshot: sourceSnapshot } : {}),
        },
      )
    : await ProductServiceAPI.createDatabaseRuleset(databaseId, {
        name: input.name,
        source_filename: input.sourceFilename,
        source_extraction_id: input.sourceExtractionId,
        source_snapshot: sourceSnapshot ?? null,
        rules,
      });
  return success
    ? { success: true, ruleset: result as ImportRuleset }
    : {
        success: false,
        error: errorText(result, 'Failed to save the ruleset'),
      };
};

export const fetchRulesetChanges = async (
  databaseId: string,
  rulesetId: string,
  page: number,
  pageSize: number,
): Promise<{
  success: boolean;
  changes?: RulesetChangesPage;
  error?: string;
}> => {
  const { success, result } = await ProductServiceAPI.getDatabaseRulesetChanges(
    databaseId,
    rulesetId,
    { page, page_size: pageSize },
  );
  return success
    ? { success: true, changes: result as RulesetChangesPage }
    : {
        success: false,
        error: errorText(result, 'Failed to load the ruleset changes'),
      };
};

export const fetchProductRulesetEffects = async (
  databaseId: string,
  databaseProductId: string,
): Promise<ProductRulesetEffects | null> => {
  const { success, result } = await ProductServiceAPI.getProductRulesetEffects(
    databaseId,
    databaseProductId,
  );
  return success ? (result as ProductRulesetEffects) : null;
};

/** Copy a ruleset, inactive, into another database of the same team. */
export const copyImportRuleset = async (
  databaseId: string,
  rulesetId: string,
  targetDatabaseId: string,
): Promise<{ success: boolean; ruleset?: ImportRuleset; error?: string }> => {
  const { success, result } = await ProductServiceAPI.copyDatabaseRuleset(
    databaseId,
    rulesetId,
    targetDatabaseId,
  );
  return success
    ? { success: true, ruleset: result as ImportRuleset }
    : {
        success: false,
        error: errorText(result, 'Failed to copy the ruleset'),
      };
};

type JobAction = 'apply' | 'deactivate' | 'delete';

/**
 * Start a ruleset job. Deleting an inactive ruleset finishes at once, with no
 * job to follow (jobId undefined).
 */
export const startImportRulesetJob = async (
  databaseId: string,
  rulesetId: string,
  action: JobAction,
): Promise<{ success: boolean; jobId?: string; error?: string }> => {
  const call =
    action === 'apply'
      ? ProductServiceAPI.applyDatabaseRuleset
      : action === 'deactivate'
      ? ProductServiceAPI.deactivateDatabaseRuleset
      : ProductServiceAPI.deleteDatabaseRuleset;
  const { success, result } = await call(databaseId, rulesetId);
  return success
    ? { success: true, jobId: (result as { jobId?: string })?.jobId }
    : {
        success: false,
        error: errorText(result, 'The ruleset change could not be started'),
      };
};

const wait = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });

/**
 * Follow a ruleset job to the end. Resolves with the finished job when it
 * completes and rejects with its error (the job is attached as `job`) when it
 * fails or is cancelled. Large databases can take a while, so there is no
 * time limit; aborting `signal` stops following it (the job keeps running).
 */
export const pollImportRulesetJob = async (
  databaseId: string,
  jobId: string,
  options: {
    signal?: AbortSignal;
    onUpdate?: (job: ImportRulesetJob) => void;
  } = {},
): Promise<ImportRulesetJob> => {
  for (;;) {
    await wait(POLL_INTERVAL_MS, options.signal);
    const { success, result } = await ProductServiceAPI.getDatabaseRulesetJob(
      databaseId,
      jobId,
    );
    if (!success) {
      throw new Error(
        errorText(result, 'Could not check on the ruleset change'),
      );
    }
    const job = result as ImportRulesetJob;
    if (job.status === 'completed') return job;
    if (job.status === 'failed' || job.status === 'cancelled') {
      throw Object.assign(
        new Error(job.error || 'The ruleset change did not finish'),
        { job },
      );
    }
    options.onUpdate?.(job);
  }
};
